import { useState } from 'react';
import { Screen } from '../../app/Frame';
import { useMeContext, useWritable } from '../../app/MeContext';
import { useScreen, useSheet, useToast } from '../../app/shell-context';
import { ApiError } from '../../api/client';
import { addDaysISO, formatISODay, formatTime, toLocal, todayISO, weekdayOfISO } from '../../lib/time';
import { plural } from '../../lib/plural';
import { Notice } from '../../ui/parts';
import { AppointmentSheet } from '../AppointmentSheet';
import {
  useAnnounceWeek, useAppointmentAction, useCreateAppointment, useSchedule,
  useStaffChildren, useStaffSlots, useStaffWeek,
} from '../api';
import type { AgendaItem } from '../types';

/** Понедельник той недели, в которую попадает дата. 0 = понедельник и на сервере, и здесь. */
const mondayOf = (dateISO: string) => addDaysISO(dateISO, -weekdayOfISO(dateISO));

/**
 * Вкладка «Календарь»: неделя целиком, чтобы перед её началом разложить занятия.
 *
 * Раскладка — лента дней сверху вниз, а не сетка семи колонок: на 375px колонка
 * шириной 50px не вмещает ни имени ребёнка, ни времени.
 *
 * Занятия не перетаскиваются, а переносятся тапом по подсвеченному окну. Это не
 * упрощение: сервер принимает перенос только на узел своей сетки слотов (шаг равен
 * длительности услуги, минус буфер, занятость ребёнка у коллег, запас перед записью
 * и горизонт). Допустимые цели всё равно нужно показывать — иначе человек тыкается
 * в отказы, — а когда они показаны, тап по цели короче и надёжнее перетаскивания.
 */
export function Calendar() {
  const me = useMeContext();
  const tz = me.center.tz;
  const tf = me.center.timeFormat;
  const writable = useWritable();
  const sheet = useSheet();
  const toast = useToast();
  const action = useAppointmentAction();

  const [monday, setMonday] = useState(() => mondayOf(todayISO(tz)));
  const sunday = addDaysISO(monday, 6);
  const week = useStaffWeek(monday, sunday);
  const items = week.data?.items ?? [];

  /** Что сейчас ставим в окно: переносим существующее занятие или заводим новое. */
  const [moving, setMoving] = useState<AgendaItem | null>(null);
  const [adding, setAdding] = useState<{ childId: number; serviceId: number; who: string } | null>(null);
  const picking = moving !== null || adding !== null;

  const slots = useStaffSlots({
    specialistId: me.specialistId ?? null,
    serviceId: moving?.service_id ?? adding?.serviceId ?? null,
    childId: moving?.child_id ?? adding?.childId ?? null,
    from: monday,
    to: sunday,
    exceptAppointmentId: moving?.id,
  });
  const create = useCreateAppointment();
  const announce = useAnnounceWeek();
  const pending = week.data?.pendingChanges ?? 0;

  useScreen('Календарь', null);

  const days = Array.from({ length: 7 }, (_, i) => addDaysISO(monday, i));
  const byDay = (date: string) => items.filter((a) => toLocal(a.starts_at_utc, tz).date === date);
  const freeOn = (date: string) => (picking ? slots.data?.find((d) => d.date === date)?.slots ?? [] : []);

  const stop = () => { setMoving(null); setAdding(null); };

  /** Ошибки слота одинаковы для переноса и постановки: решает сервер, мы показываем. */
  const explain = (e: unknown, fallback: string) => {
    if (e instanceof ApiError && e.is('CHILD_BUSY')) {
      const other = e.details as { display_name?: string } | undefined;
      return other?.display_name
        ? `В это время ребёнок занят у коллеги — ${other.display_name}`
        : 'В это время ребёнок занят у другого специалиста';
    }
    return e instanceof ApiError ? e.message : fallback;
  };

  function put(startsAt: number) {
    if (moving) {
      action.mutate({ id: moving.id, action: 'reschedule', startsAt }, {
        onSuccess: () => { toast('Занятие перенесено'); stop(); },
        onError: (e) => toast(explain(e, 'Не получилось перенести')),
      });
      return;
    }
    if (!adding) return;
    create.mutate({ childId: adding.childId, serviceId: adding.serviceId, startsAt }, {
      onSuccess: () => { toast('Занятие поставлено'); stop(); },
      onError: (e) => toast(explain(e, 'Не получилось поставить')),
    });
  }

  const busy = action.isPending || create.isPending;
  const thisWeek = monday === mondayOf(todayISO(tz));

  return (
    <Screen>
      <div className="daynav">
        <button type="button" aria-label="Предыдущая неделя" onClick={() => setMonday(addDaysISO(monday, -7))}>‹</button>
        <b>{weekTitle(monday, sunday)}</b>
        <button type="button" aria-label="Следующая неделя" onClick={() => setMonday(addDaysISO(monday, 7))}>›</button>
      </div>

      {thisWeek ? null : (
        <button className="btn" onClick={() => setMonday(mondayOf(todayISO(tz)))}>Вернуться к текущей неделе</button>
      )}

      {picking ? (
        <Notice kind="calm">
          {moving ? `Переносим: ${moving.child_name}` : `Ставим: ${adding!.who}`}. Выберите подсвеченное окно.
          {slots.isPending ? ' Считаем свободное время…' : null}
          {' '}
          <button className="linkish" type="button" onClick={stop}>Отмена</button>
        </Notice>
      ) : null}

      {week.isPending ? <p className="lead">Загружаем неделю…</p> : null}

      {days.map((date) => {
        const dayItems = byDay(date);
        const free = freeOn(date);
        return (
          <div key={date}>
            <p className="eyebrow">{formatISODay(date)}</p>
            <div className="timeline" style={{ marginTop: 8 }}>
              {dayItems.map((a) => (
                <div className="tl" key={a.id}>
                  <span className="tl-time">{formatTime(a.starts_at_utc, tz, tf)}</span>
                  <button className="tl-body tl-open" type="button" onClick={() => sheet.open(<AppointmentSheet item={a} />)}>
                    <b>{a.child_name}</b>
                    <span>{a.service_title}</span>
                  </button>
                  {writable && !picking ? (
                    <button className="btn tl-move" type="button" onClick={() => setMoving(a)}>Перенести</button>
                  ) : null}
                </div>
              ))}

              {free.map((s) => (
                <button
                  className="tl gap tl-free"
                  type="button"
                  key={s.startsAt}
                  disabled={busy}
                  onClick={() => put(s.startsAt)}
                >
                  <span className="tl-time">{formatTime(s.startsAt, tz, tf)}</span>
                  <span className="tl-body"><b>Свободно — поставить сюда</b></span>
                </button>
              ))}

              {!dayItems.length && !free.length ? (
                <p className="lead">{picking ? 'Свободных окон нет' : 'Занятий нет'}</p>
              ) : null}
            </div>
          </div>
        );
      })}

      {writable && !picking ? (
        <button className="add" onClick={() => sheet.open(<AddSheet onPick={(v) => setAdding(v)} />)}>
          ＋ Записать ребёнка
        </button>
      ) : null}

      {/* Переносы применились сразу — родителям о них сообщаем одним письмом, когда
          неделя разложена. Иначе за один разбор ушло бы три письма подряд. */}
      {writable && !picking ? (
        <>
          <button
            className="btn solid"
            disabled={!pending || announce.isPending}
            onClick={() => announce.mutate({ from: monday, to: sunday }, {
              onSuccess: (r) => toast(r.sent
                ? `Родителям отправлено ${r.sent} ${plural(r.sent, 'сообщение', 'сообщения', 'сообщений')}`
                : 'Изменений нет'),
              onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось отправить'),
            })}
          >
            {announce.isPending ? 'Отправляем…' : 'Утвердить неделю'}
          </button>
          <p className="footnote" style={{ textAlign: 'left' }}>
            {pending
              ? `${pending} ${plural(pending, 'изменение', 'изменения', 'изменений')} — родители ещё не знают`
              : 'Все изменения разосланы'}
          </p>
        </>
      ) : null}
    </Screen>
  );
}

/**
 * «24–30 августа» или «29 сентября – 5 октября», если неделя перешла через месяц.
 * Месяц берём из formatISODay: Intl с `month:'long'` даёт именительный («август»),
 * а рядом с числом нужен родительный.
 */
function weekTitle(from: string, to: string): string {
  const full = (d: string) => formatISODay(d, { weekday: false });
  return from.slice(0, 7) === to.slice(0, 7)
    ? `${Number(from.slice(8))}–${full(to)}`
    : `${full(from)} – ${full(to)}`;
}

/**
 * Кого и на что ставим. Ребёнок — только закреплённый (иначе сервер откажет), услуга —
 * только та, которую специалист ведёт: список берём из его же условий оплаты.
 */
function AddSheet({ onPick }: { onPick: (v: { childId: number; serviceId: number; who: string }) => void }) {
  const sheet = useSheet();
  const children = useStaffChildren();
  const schedule = useSchedule();

  const kids = children.data?.pages.flatMap((p) => p.items) ?? [];
  const services = (schedule.data?.rates ?? []).filter((r) => r.is_active);

  const [childId, setChildId] = useState<number | null>(null);
  const [serviceId, setServiceId] = useState<number | null>(null);
  const kid = kids.find((k) => k.id === childId) ?? null;
  const svc = services.find((r) => r.service_id === serviceId) ?? null;

  return (
    <>
      <h3>Записать ребёнка</h3>
      <p>После выбора подсветим свободные окна недели — поставьте занятие тапом.</p>

      <div className="field">
        <label htmlFor="addKid">Ребёнок</label>
        <select id="addKid" value={childId ?? ''} onChange={(e) => setChildId(Number(e.target.value))}>
          <option value="" disabled>Выберите</option>
          {kids.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
        </select>
        {kids.length ? null : <span className="hint">За вами пока никто не закреплён.</span>}
      </div>

      <div className="field">
        <label htmlFor="addSvc">Услуга</label>
        <select id="addSvc" value={serviceId ?? ''} onChange={(e) => setServiceId(Number(e.target.value))}>
          <option value="" disabled>Выберите</option>
          {services.map((r) => (
            <option key={r.service_id} value={r.service_id}>{r.title} · {r.duration_min} мин</option>
          ))}
        </select>
      </div>

      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Отмена</button>
        <button
          className="btn solid"
          disabled={!kid || !svc}
          onClick={() => {
            onPick({ childId: kid!.id, serviceId: svc!.service_id, who: `${kid!.name} · ${svc!.title}` });
            sheet.close();
          }}
        >
          Выбрать окно
        </button>
      </div>
    </>
  );
}

